import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Checkbox, FormControlLabel,
  MenuItem, Stack, TextField, Typography
} from '@mui/material'
import { getSharingDirectory, sharingRequest } from '../service'
import { useAuth } from '../auth/AuthContext'

export default function Groups () {
  const { refreshSharing } = useAuth()
  const [directory, setDirectory] = useState(null)
  const [name, setName] = useState('')
  const [kind, setKind] = useState('family')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [members, setMembers] = useState({})
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('viewer')
  const [inviteToken, setInviteToken] = useState('')
  const [acceptToken, setAcceptToken] = useState('')
  const load = useCallback(async () => {
    const d = await getSharingDirectory()
    setDirectory(d)
    await refreshSharing()
    return d
  }, [refreshSharing])
  useEffect(() => { load().catch(e => setError(e.message)) }, [load])
  const run = async action => {
    setBusy(true)
    setError(null)
    try { await action(); await load() } catch (e) { setError(e.message) } finally { setBusy(false) }
  }
  const request = (path, method, body) => sharingRequest(path, {
    method, ...(body ? { body: JSON.stringify(body) } : {})
  })
  const loadMembers = async id => {
    const data = await sharingRequest(`/groups/${id}/members`)
    setMembers(current => ({ ...current, [id]: data }))
  }
  const enabled = directory?.mode === 'application'
  return <Box sx={{ maxWidth: 1000, mx: 'auto', py: 3 }}>
    <Typography variant="h4" gutterBottom>Sharing groups</Typography>
    <Typography color="text.secondary" sx={{ mb: 2 }}>
      Your groups are private, invitation-only collections. A display name such as Family does not connect unrelated accounts.
      Changing a default affects new uploads only. Existing upload and clip sharing stays unchanged.
    </Typography>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {directory && !enabled && <Alert severity="info" sx={{ mb: 2 }}>
      Existing sharing is still using Cognito. Group management will activate only after membership migration passes its permission checks.
    </Alert>}
    <Stack spacing={2}>
      <Card variant="outlined"><CardContent>
        <Typography variant="h6">Create a group</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField label="Group name" value={name} onChange={e => setName(e.target.value)} />
          <TextField select label="Type" value={kind} onChange={e => setKind(e.target.value)} sx={{ minWidth: 150 }}>
            {['family', 'team', 'coaches', 'custom'].map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
          </TextField>
          <Button disabled={!enabled || busy || !name.trim()} onClick={() => run(async () => {
            await request('/groups', 'POST', { name, kind }); setName('')
          })}>Create private group</Button>
        </Stack>
      </CardContent></Card>
      {(directory?.groups || []).map(g => <Card key={g.id} variant="outlined"><CardContent>
        <Typography variant="h6">{g.display_name}</Typography>
        <Typography variant="body2" color="text.secondary">{g.kind} · {g.role} · Group {g.id.slice(0, 8)}</Typography>
        <FormControlLabel label="Share new uploads with this group by default" control={<Checkbox
          disabled={!enabled || busy || g.role === 'viewer'}
          checked={(directory.default_group_ids || []).includes(g.id)}
          onChange={e => {
            const next = new Set(directory.default_group_ids)
            if (e.target.checked) next.add(g.id); else next.delete(g.id)
            run(() => request('/defaults', 'PATCH', { group_ids: [...next] }))
          }} />} />
        {g.role === 'manager' && enabled && <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ my: 2 }}>
            <TextField label="Recipient email" value={email} onChange={e => setEmail(e.target.value)} />
            <TextField select label="Access" value={role} onChange={e => setRole(e.target.value)} sx={{ minWidth: 150 }}>
              {['viewer', 'contributor'].map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <Button disabled={busy || !email} onClick={() => run(async () => {
              const result = await request(`/groups/${g.id}/invitations`, 'POST', { email, role })
              setInviteToken(result.token)
            })}>Create invitation</Button>
            <Button disabled={busy} onClick={() => run(() => loadMembers(g.id))}>Show members</Button>
          </Stack>
          {(members[g.id]?.members || []).map(m => <Stack key={m.subject} direction="row" alignItems="center" spacing={2} sx={{ my: 1 }}>
            <Typography sx={{ flex: 1 }}>{m.username || m.subject} · {m.role} · {m.status}</Typography>
            {m.status === 'active' && m.subject !== g.owner_sub && <Button disabled={busy} color="warning" onClick={() => {
              if (window.confirm(`Remove ${m.username || 'this member'} from ${g.display_name}?`)) {
                run(async () => {
                  await request(`/groups/${g.id}/members/${m.subject}`, 'PATCH', {
                    status: 'removed', expected_revision: members[g.id].revision
                  }); await loadMembers(g.id)
                })
              }
            }}>Remove</Button>}
          </Stack>)}
        </>}
        {!g.owner_sub && <Typography variant="caption" color="text.secondary">
          Legacy group. Membership is preserved; a verified owner must be assigned before management is enabled.
        </Typography>}
      </CardContent></Card>)}
      {inviteToken && <Alert severity="success">
        Invitation created. No email was sent. Share this private token with the intended recipient; it expires in seven days.
        <TextField fullWidth value={inviteToken} inputProps={{ readOnly: true }} sx={{ mt: 1 }} />
      </Alert>}
      <Card variant="outlined"><CardContent>
        <Typography variant="h6">Accept an invitation</Typography>
        <Typography variant="body2" color="text.secondary">Sign in with the verified email specified by the person who invited you.</Typography>
        <TextField fullWidth label="Private invitation token" value={acceptToken} onChange={e => setAcceptToken(e.target.value)} sx={{ my: 2 }} />
        <Button disabled={!enabled || busy || !acceptToken} onClick={() => run(async () => {
          await request('/invitations/accept', 'POST', { token: acceptToken }); setAcceptToken('')
        })}>Accept invitation</Button>
      </CardContent></Card>
    </Stack>
  </Box>
}
